import React from "react";
import { Link } from "react-router-dom";
import * as hrmsService from "../../services/hrmsService";
import { EmptyState, LoadState, PageHeader } from "./hrComponents";
import { cardStyle, formatDate, pageStyle, useLoad } from "./hrUi";

// HR's landing page: headcount, who is away today, what is waiting, what is about to expire.
function Stat({ label, value, to, hint }) {
  const body = (
    <div style={{ ...cardStyle, padding: "16px 20px", minWidth: 180, flex: "1 1 180px" }}>
      <div style={{ fontSize: 13, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>{value}</div>
      {hint ? <div style={{ fontSize: 12, color: "#6B7280" }}>{hint}</div> : null}
    </div>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: "none", color: "inherit", flex: "1 1 180px" }}>
      {body}
    </Link>
  ) : (
    body
  );
}

export default function HrOverview() {
  const people = useLoad(() => hrmsService.listEmployees({ size: 1 }), []);
  const onNotice = useLoad(() => hrmsService.listEmployees({ status: "ON_NOTICE", size: 1 }), []);
  const away = useLoad(() => hrmsService.getWhosAway(), []);
  const waiting = useLoad(() => hrmsService.listLeaveRequests({ status: "PENDING" }), []);
  const approvals = useLoad(() => hrmsService.getApprovalCount(), []);
  const expiring = useLoad(() => hrmsService.getExpiringDocuments(30), []);

  const awayRows = away.data?.away || [];
  const expiringRows = expiring.data || [];

  return (
    <div style={pageStyle}>
      <PageHeader title="HR Overview" subtitle="Today at a glance" />
      <div className="d-flex flex-wrap gap-3 mb-4">
        <Stat label="Employees" value={people.data ? people.data.total : "…"} to="/hr/employees" hint={onNotice.data ? `${onNotice.data.total} on notice` : undefined} />
        <Stat label="Away today" value={away.data ? awayRows.length : "…"} to="/hr/leave" />
        <Stat label="Leave to approve" value={waiting.data ? waiting.data.length : "…"} to="/hr/leave" />
        <Stat label="Waiting for me" value={approvals.data ? approvals.data.pending : "…"} to="/hr/approvals" />
        <Stat label="Documents expiring (30 days)" value={expiring.data ? expiringRows.length : "…"} to="/hr/documents" />
      </div>

      <h6 className="mb-2">Away today</h6>
      <LoadState loading={away.loading} error={away.error} onRetry={away.reload} rows={2}>
        {awayRows.length === 0 ? (
          <EmptyState title="Everyone is in" />
        ) : (
          <div style={{ ...cardStyle, padding: "4px 18px" }}>
            {awayRows.map((a) => (
              <div key={`${a.employee_id}${a.leave_request_id}`} className="d-flex justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <span>{a.full_name}</span>
                <span style={{ color: "#6B7280" }}>
                  {a.leave_type_name}
                  {a.fraction < 1 ? " (half day)" : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </LoadState>

      <h6 className="mt-4 mb-2">Documents expiring soon</h6>
      <LoadState loading={expiring.loading} error={expiring.error} onRetry={expiring.reload} rows={2}>
        {expiringRows.length === 0 ? (
          <EmptyState title="Nothing is about to expire" />
        ) : (
          <div style={{ ...cardStyle, padding: "4px 18px" }}>
            {expiringRows.slice(0, 8).map((d) => (
              <div key={d.id} className="d-flex justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <span>
                  {d.full_name} · {d.type_name}
                </span>
                <span style={{ color: d.days_left < 0 ? "#B91C1C" : "#92400E" }}>{formatDate(d.expiry_date)}</span>
              </div>
            ))}
          </div>
        )}
      </LoadState>
    </div>
  );
}
